import { LabelType, LabelItem } from "./labelTypes"

/**
 * Parses the questionnaire content to extract all labels and lists
 *
 * @param questionnaireContent - The raw questionnaire content as a string
 * @returns An array of LabelItem objects representing all labels and lists found
 */
export const parseLabelsAndLists = (
  questionnaireContent: string
): LabelItem[] => {
  const lines = questionnaireContent.split("\n")
  const labelItems: LabelItem[] = []
  const labelMap = new Map<string, LabelItem>() // To track unique labels

  // First pass: Find all list definitions
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const trimmedLine = line.trim()

    // Skip empty lines and comments (except LIST-related comments)
    if (
      !trimmedLine ||
      (trimmedLine.startsWith("#") && !trimmedLine.startsWith("#DEFINE"))
    ) {
      i++
      continue
    }

    // Parse Question Labels (e.g., "Q Team: Which team do you belong to?" or "Q Household Income: Which...")
    // Note: Question labels are only defined once, no need to track occurrences
    // Label can contain any characters including template placeholders like {{question label}}
    const questionLabelMatch = line.match(/^Q\s+(.+?):/)
    if (questionLabelMatch) {
      const labelName = questionLabelMatch[1].trim()
      const labelId = `QUESTION_${labelName.replace(/\s+/g, "_")}`

      if (!labelMap.has(labelId)) {
        const labelItem: LabelItem = {
          id: labelId,
          type: LabelType.QUESTION,
          name: labelName,
          content: line,
          lineNumbers: [i + 1],
          originalLines: [line],
        }
        labelItems.push(labelItem)
        labelMap.set(labelItem.id, labelItem)
      }
      i++
      continue
    }

    // Parse EXCEPTIONS (standalone or with LIST keyword)
    // Note: Exceptions are only defined once
    if (trimmedLine === "EXCEPTIONS" || trimmedLine === "LIST EXCEPTIONS") {
      const labelId = "EXCEPTIONS_EXCEPTIONS"

      if (!labelMap.has(labelId)) {
        const contentLines: string[] = [line]
        const startLine = i
        i++

        // Collect exception items until we hit an empty line, comment, or another definition
        while (i < lines.length) {
          const currentLine = lines[i]
          const currentTrimmed = currentLine.trim()

          if (
            !currentTrimmed ||
            currentTrimmed.startsWith("#") ||
            currentTrimmed.startsWith("Q ") ||
            currentTrimmed.startsWith("LIST ") ||
            currentTrimmed.startsWith("RANGE LIST ") ||
            currentTrimmed.startsWith("RANDOMIZE ") ||
            currentTrimmed.startsWith("NOTE ") ||
            currentTrimmed.startsWith("TEXT ") ||
            currentTrimmed === "EXCEPTIONS"
          ) {
            break
          }

          contentLines.push(currentLine)
          i++
        }

        const labelItem: LabelItem = {
          id: labelId,
          type: LabelType.EXCEPTIONS,
          name: "EXCEPTIONS",
          content: contentLines.join("\n"),
          lineNumbers: [startLine + 1],
          originalLines: contentLines,
        }
        labelItems.push(labelItem)
        labelMap.set(labelItem.id, labelItem)
      } else {
        // Skip duplicate exceptions block
        i++
        while (i < lines.length) {
          const currentLine = lines[i]
          const currentTrimmed = currentLine.trim()
          if (
            !currentTrimmed ||
            currentTrimmed.startsWith("#") ||
            currentTrimmed.startsWith("Q ") ||
            currentTrimmed.startsWith("LIST ") ||
            currentTrimmed.startsWith("RANGE LIST ") ||
            currentTrimmed.startsWith("RANDOMIZE ") ||
            currentTrimmed.startsWith("NOTE ") ||
            currentTrimmed.startsWith("TEXT ") ||
            currentTrimmed === "EXCEPTIONS"
          ) {
            break
          }
          i++
        }
      }
      continue
    }

    // Parse RANGE LIST (e.g., "RANGE LIST Scale_Agreement: 1..5")
    // Allow template placeholders like {{list name}}
    const rangeListMatch = line.match(/^RANGE\s+LIST\s+(.+?):\s*(.+)/)
    if (rangeListMatch) {
      const listName = rangeListMatch[1].trim()
      const labelId = `RANGE_LIST_${listName}`

      if (!labelMap.has(labelId)) {
        const contentLines: string[] = [line]
        const startLine = i
        i++

        // Collect scale items until we hit an empty line, comment, or another definition
        while (i < lines.length) {
          const currentLine = lines[i]
          const currentTrimmed = currentLine.trim()

          // Check if this line looks like a scale item (e.g., "1: Strongly disagree")
          if (currentTrimmed && /^\d+:/.test(currentTrimmed)) {
            contentLines.push(currentLine)
            i++
            continue
          }

          // Stop if we hit another definition, comment, or empty line
          if (
            !currentTrimmed ||
            currentTrimmed.startsWith("#") ||
            currentTrimmed.startsWith("Q ") ||
            currentTrimmed.startsWith("LIST ") ||
            currentTrimmed.startsWith("RANGE LIST ") ||
            currentTrimmed.startsWith("RANDOMIZE ") ||
            currentTrimmed.startsWith("NOTE ") ||
            currentTrimmed.startsWith("TEXT ") ||
            currentTrimmed === "EXCEPTIONS"
          ) {
            break
          }

          i++
        }

        const labelItem: LabelItem = {
          id: labelId,
          type: LabelType.RANGE_LIST,
          name: listName,
          content: contentLines.join("\n"),
          lineNumbers: [startLine + 1],
          originalLines: contentLines,
          referenceLineNumbers: [],
          referenceLines: [],
        }
        labelItems.push(labelItem)
        labelMap.set(labelItem.id, labelItem)
      } else {
        // Skip duplicate range list definition
        i++
        while (i < lines.length) {
          const currentLine = lines[i]
          const currentTrimmed = currentLine.trim()
          if (currentTrimmed && /^\d+:/.test(currentTrimmed)) {
            i++
            continue
          }
          if (
            !currentTrimmed ||
            currentTrimmed.startsWith("#") ||
            currentTrimmed.startsWith("Q ") ||
            currentTrimmed.startsWith("LIST ") ||
            currentTrimmed.startsWith("RANGE LIST ") ||
            currentTrimmed.startsWith("RANDOMIZE ") ||
            currentTrimmed.startsWith("NOTE ") ||
            currentTrimmed.startsWith("TEXT ") ||
            currentTrimmed === "EXCEPTIONS"
          ) {
            break
          }
          i++
        }
      }
      continue
    }

    // Parse regular LIST (e.g., "LIST Fundraising Competitors")
    const listMatch = line.match(/^LIST\s+(.+)/)
    if (listMatch && !line.match(/^LIST\s+EXCEPTIONS/)) {
      const listName = listMatch[1].trim()
      const labelId = `LIST_${listName}`

      if (!labelMap.has(labelId)) {
        const contentLines: string[] = [line]
        const startLine = i
        i++

        // Collect list items until we hit an empty line, comment, or another definition
        while (i < lines.length) {
          const currentLine = lines[i]
          const currentTrimmed = currentLine.trim()

          if (
            !currentTrimmed ||
            currentTrimmed.startsWith("#") ||
            currentTrimmed.startsWith("Q ") ||
            currentTrimmed.startsWith("LIST ") ||
            currentTrimmed.startsWith("RANGE LIST ") ||
            currentTrimmed.startsWith("RANDOMIZE ") ||
            currentTrimmed.startsWith("NOTE ") ||
            currentTrimmed.startsWith("TEXT ") ||
            currentTrimmed === "EXCEPTIONS"
          ) {
            break
          }

          contentLines.push(currentLine)
          i++
        }

        const labelItem: LabelItem = {
          id: labelId,
          type: LabelType.LIST,
          name: listName,
          content: contentLines.join("\n"),
          lineNumbers: [startLine + 1],
          originalLines: contentLines,
          referenceLineNumbers: [],
          referenceLines: [],
        }
        labelItems.push(labelItem)
        labelMap.set(labelItem.id, labelItem)
      } else {
        // Skip duplicate list definition
        i++
        while (i < lines.length) {
          const currentLine = lines[i]
          const currentTrimmed = currentLine.trim()
          if (
            !currentTrimmed ||
            currentTrimmed.startsWith("#") ||
            currentTrimmed.startsWith("Q ") ||
            currentTrimmed.startsWith("LIST ") ||
            currentTrimmed.startsWith("RANGE LIST ") ||
            currentTrimmed.startsWith("RANDOMIZE ") ||
            currentTrimmed.startsWith("NOTE ") ||
            currentTrimmed.startsWith("TEXT ") ||
            currentTrimmed === "EXCEPTIONS"
          ) {
            break
          }
          i++
        }
      }
      continue
    }

    i++
  }

  // Second pass: Find references to lists in questions
  // Look for patterns like "PER OutputTypes" or "FROM Timeframes" or "RANDOMIZE ListName"
  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const line = lines[lineIdx]

    // Match "PER <ListName>" pattern
    // Allow template placeholders like {{list name}}
    const perMatch = line.match(/\bPER\s+(.+?)(?:\s*$|(?=\s+(?:FROM|SELECT|Q\s)))/i)
    if (perMatch) {
      const listName = perMatch[1].trim()
      const listLabel = labelMap.get(`LIST_${listName}`)
      if (listLabel) {
        listLabel.referenceLineNumbers = listLabel.referenceLineNumbers || []
        listLabel.referenceLines = listLabel.referenceLines || []
        if (!listLabel.referenceLineNumbers.includes(lineIdx + 1)) {
          listLabel.referenceLineNumbers.push(lineIdx + 1)
          listLabel.referenceLines.push(line)
        }
      }
    }

    // Match "FROM <ListName>" pattern (handles both on same line and next line)
    // Allow template placeholders like {{list name}}
    const fromMatchSameLine = line.match(
      /\bFROM\s+(.+?)(?:\s*$|\s*\|)/i
    )
    if (fromMatchSameLine) {
      const listName = fromMatchSameLine[1].trim()
      const listLabel = labelMap.get(`LIST_${listName}`)
      if (listLabel) {
        listLabel.referenceLineNumbers = listLabel.referenceLineNumbers || []
        listLabel.referenceLines = listLabel.referenceLines || []
        if (!listLabel.referenceLineNumbers.includes(lineIdx + 1)) {
          listLabel.referenceLineNumbers.push(lineIdx + 1)
          listLabel.referenceLines.push(line)
        }
      }
    }

    // Match "RANDOMIZE <ListName>" pattern
    // Allow template placeholders like {{list name}}
    const randomizeMatch = line.match(/\bRANDOMIZE\s+(.+?)(?:\s*$)/i)
    if (randomizeMatch) {
      const listName = randomizeMatch[1].trim()
      const listLabel = labelMap.get(`LIST_${listName}`)
      if (listLabel) {
        listLabel.referenceLineNumbers = listLabel.referenceLineNumbers || []
        listLabel.referenceLines = listLabel.referenceLines || []
        if (!listLabel.referenceLineNumbers.includes(lineIdx + 1)) {
          listLabel.referenceLineNumbers.push(lineIdx + 1)
          listLabel.referenceLines.push(line)
        }
      }
    }

    // Match "SELECT ONE FROM" or "SELECT MULTIPLE FROM" where list name is on next line
    const selectFromMatch = line.match(/SELECT\s+(?:ONE|MULTIPLE)\s+FROM$/i)
    if (selectFromMatch && lineIdx + 1 < lines.length) {
      const nextLine = lines[lineIdx + 1].trim()
      // Check if next line is a list name
      const listLabel = labelMap.get(`LIST_${nextLine}`)
      if (listLabel) {
        listLabel.referenceLineNumbers = listLabel.referenceLineNumbers || []
        listLabel.referenceLines = listLabel.referenceLines || []
        if (!listLabel.referenceLineNumbers.includes(lineIdx + 2)) {
          listLabel.referenceLineNumbers.push(lineIdx + 2)
          listLabel.referenceLines.push(lines[lineIdx + 1])
        }
      }
    }

    // Also check for RANGE LIST references in SELECT ONE FROM
    // Allow template placeholders like {{list name}}
    const selectFromRangeMatch = line.match(
      /SELECT\s+ONE\s+FROM\s+(.+?)(?:\s*$|\s*\|)/i
    )
    if (selectFromRangeMatch) {
      const listName = selectFromRangeMatch[1].trim()
      const rangeListLabel = labelMap.get(`RANGE_LIST_${listName}`)
      if (rangeListLabel) {
        rangeListLabel.referenceLineNumbers =
          rangeListLabel.referenceLineNumbers || []
        rangeListLabel.referenceLines = rangeListLabel.referenceLines || []
        if (!rangeListLabel.referenceLineNumbers.includes(lineIdx + 1)) {
          rangeListLabel.referenceLineNumbers.push(lineIdx + 1)
          rangeListLabel.referenceLines.push(line)
        }
      }
    }
  }

  return labelItems
}
